/*global _ */
import {nil, notEmpty, empty, notNil, hasSome, templify, map, fv} from '../../../lib/formatters'
import MashupBuilderReactiveMixin from '../../../mixins/mashup-builder/reactives'

const mixins = [MashupBuilderReactiveMixin]

export default {
  name: 're-content-sections',
  mixins,
  props: {
    sections: {
      type: [Array, Object]
    },
    component: {
      type: String,
      default: 're-group-box'
    },
    itemComponent: {
      type: String,
      default: 're-blank-component'
    },
    parseData: {
      type: [Boolean, Number, String],
      default: false
    },
    data: {}
  },
  data() {
    return {
      nil,
      notNil,
      empty,
      notEmpty,
      sectionsData: {}
    }
  },
  computed: {
    sectionComponent() {
      return this.component || 're-blank-component'
    },
    sectionItemComponent() {
      return this.itemComponent || 're-blank-component'
    },
    isGroupBox() {
      return this.sectionComponent === 're-group-box'
    },
  },
  watch: {
    sections: {
      handler(v) {
        this.reload(v)
      },
      deep: true
    }
  },
  created() {
    this.reload()
  },
  methods: {
    reload(data) {
      this.sectionsData = this.prepare(data || this.sections)
    },
    prepare(sections) {
      return map(sections, (section, sectionKey) => {
        return {
          classes: section.classes,
          showTitle: section.showTitle,
          ...section,
          key: `sections.${sectionKey}`,
          show: this.showSection(section),
          component: this.getSectionComponent(section),
          componentAttrs: this.getComponentAttr(section),
          listeners: this.getComponentListeners(section),
          title: this.parseText(section.title, section),
          showBoxTitle: !this.isGroupBox && fv(section.showTitle, section.title),
          boxTitle: this.parseText(section.title, {}, section),
          showBoxSubTitle: fv(section.showSubTitle, section.subTitle),
          boxSubTitle: this.parseText(section.subTitle, section),
          boxSubTitleClasses: {'re-group-box-subtitle' : this.isGroupBox},
          showBoxNotice: fv(section.showNotice, section.notice),
          boxNotice: this.parseText(section.notice, section),
          items: this.prepareItems(section, sectionKey),
          actions: this.prepareActions(section, sectionKey),
        }
      })
    },
    prepareItems(section, sectionKey) {
      if (!section.items) {
        return {}
      }
      let items = map(section.items, (item, key) => {
        return {
          prefix: '',
          suffix: '',
          promising: false,
          defaultValue: '',
          valueComponent: null,
          dynamic: item.dynamic,
          ...item,
          key: `${sectionKey}.items.${key}`,
          show: this.showItem(item, section),
          component: this.getItemComponent(item, section),
          componentAttrs: this.getComponentAttr(item),
          listeners: this.getComponentListeners(item),
          classes: [item.classes, item.dynamic ? 're-section-dynamic-item': 're-section-item'],
          title: this.parseText(item.title, section, item),
          showTitle: this.showItemTitle(item, section),
        }
      })
      return map(items, (item, key) => {
        const value = item.value = this.parseValue(item, section)
        item.showValue = this.showValue(value, item, section)
        return item
      })
    },
    prepareActions(section, sectionKey) {
      if (!section.actions) {
        return null
      }
      return map(section.actions, (action, actionKey) => {
        return {
          classes: action.classes,
          ...action,
          key: `${sectionKey}.actions.${actionKey}`,
          component: this.getActionComponent(action, section),
          componentAttrs: this.getComponentAttr(action),
          listeners: this.getComponentListeners(action),
        }
      })
    },
    getComponentAttr(item) {
      const attr = _.omitBy(item, (i, k) => {
        return /^[[$@]/.test(k) || _.includes(['type', 'classes', 'component'], k)
      })
      return attr
    },
    getComponentListeners(item, type) {
      const listeners = _.reduce(item, (e, a, k) => {
        if (_.startsWith(k, '@')) {
          let listenerName = k.replace(/^@/, '')
          let listener = a
          if (!_.isFunction(a) && _.isObject(a)) {
            if (a.handler) {
              listener = a.handler
            }
          }
          if (_.isFunction(listener)) {
            e[listenerName] = listener
          }
        }
        return e
      }, {})
      if (type) {
        return listeners[type]
      }
      return listeners
    },

    getSectionComponent(section) {
      return section.component || this.sectionComponent
    },
    sectionOptions(section) {
      return _.omit(section, ['items'])
    },

    getItemComponent(item, section) {
      return item.component || section.itemComponent || this.itemComponent
    },

    showSection(section) {
      let show = this.$$fv(section.show, null)
      if (_.isBoolean(show)) {
        return show
      }
      if (_.isObject(show) && show['if']) {
        return this.probeIf(show['if'], section, {}, section)
      }
      return true
    },
    showItem(item, section = {}) {
      let show = this.$$fv(item.show, empty(item) ? section.show : section.showItems, null)
      if (_.isBoolean(show)) {
        return show
      }

      if (_.isObject(show) && show['if']) {
        show = this.probeIf(show['if'], null, item, section, true)
        return show
      }

      if (nil(show)) {
        if(!this.hasValue(item)) {
          return true
        }
      }

      const value = this.parseValue(item, section, true)
      const hideBlank = this.$$fv(item.hideBlank, section.hideBlankItem)
      const hideZero = this.$$fv(item.hideZero, section.hideZeroItem)
      if ((hideBlank && nil(value)) || (hideZero && /^0(.00)?$/.test(value))) {
        return false
      }

      show = true
      return show
    },
    showItemTitle(item, section) {
      return this.$$fv(item.showTitle, section.showItemTitle)
    },

    getActionComponent(action, section) {
      const component = 're-' + (action.type || 'button')
      return component
    },

    needsParsing(item, section) {
      return this.$$fv(item.parseData, section.parseData, this.parseData)
    },
    hasValue(item) {
      return hasSome(item, ['value', 'valuePath', 'method', 'promise'])
    },

    parseText(value, section = {}, item = {}) {
      const isParse = this.needsParsing(item, section)
      if (isParse) {
        const data = this.parseSourceData(item.data || section.data || this.data || {}, value)
        value = templify(value, data)
      }
      return value
    },
    parseValue(item, section = {}, getRaw, thisValue) {
      // const debug = true
      let value = this.$$fv(thisValue, item.value, item.values)
      // if (debug) {
      //   debugLog({parseValue: item, section, getRaw, thisValue})
      //   debugLog({firstValue: value})
      // }
      const isParse = this.needsParsing(item, section)
      // if (debug) {
      //   debugLog({isParse})
      // }
      if (isParse) {
        const data = this.parseSourceData(item.data || section.data || this.data || {}, value)
        // if (debug) {
        //   debugLog({parseSorce: data})
        // }
        if (getRaw) {
          item = _.omit(item, ['format'])
        }
        value = this.parseReactiveValues(item, data)
        // if (debug) {
        //   debugLog({parsedValue: value})
        // }

        if (!getRaw && !_.isObjectLike(value)) {
          value = _.castArray(value)
        }
      }
      // if (debug) {
      //   debugLog({finalValue: value})
      // }
      if (value instanceof Promise) {
        value.then((val) => {
          item.value = val
        })
        return ''
      }
      if (_.isArray(value)) {
        value = _.map(value, (v, i) => {
          if (v instanceof Promise) {
            item.promising = true
            v.then((val) => {
              item.values[i] = val
              item.value = item.values
            })
            return ''
          }
          return v
        })
        return value
      }
      return value
    },
    async getFinalValue(value) {
      if (value instanceof Promise) {
        return await value
      }
      return value
    },
    parseDefaultValue(item, section = {}, getRaw) {
      const defaultItem = {value: item.defaultValue, parseData: this.$$fv(item.parseDefault, false)}
      const defaultValue = this.parseValue(defaultItem, section, getRaw)
      return defaultValue
    },

    showValue(value, item, section) {
      return notNil(value)
    },
    showDefaultValue(value, item, section) {
      return !this.showValue(value, item, section) && notNil(item.defaultValue)
    },

    probeIf(def, value, item, section, isParseValue) {
        if (isParseValue) {
          value = this.parseValue(item, section, true)
        }
        const data = this.parseSourceData(item.data || section.data || this.data || {}, value)
        return this.probeReactiveIf(def, data)
    },

    parseSourceData(data, value) {
      if (_.isFunction(data)) {
        data = data() || {}
      }
      data['$'] = value
      return data
    }
  },
}
